const ESCAPE_LOOKUP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => ESCAPE_LOOKUP[ch] || ch)
}

function escapeForRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function getIndentLevel(line) {
  const match = String(line || '').match(/^(\s*)/)
  return match ? match[1].length : 0
}

function isTableDivider(line) {
  const normalized = String(line || '').trim()
  if (!normalized) return false
  return /^(\|\s*:?-{3,}:?\s*)+\|?$/.test(normalized)
}

function isTableRow(line) {
  const normalized = String(line || '').trim()
  return normalized.startsWith('|') && normalized.includes('|', 1)
}

function splitTableCells(line) {
  const normalized = String(line || '').trim()
  const withoutLeading = normalized.startsWith('|') ? normalized.slice(1) : normalized
  const withoutTrailing = withoutLeading.endsWith('|') ? withoutLeading.slice(0, -1) : withoutLeading
  return withoutTrailing.split('|').map(cell => cell.trim())
}

export function renderInlineMarkdown(text) {
  if (!text) return ''

  let processed = String(text)
  const placeholders = []
  const addPlaceholder = (html) => {
    const key = `__MD_PLACEHOLDER_${placeholders.length}__`
    placeholders.push({ key, html })
    return key
  }

  // Inline code first so markdown symbols inside code are preserved.
  processed = processed.replace(/`([^`]+)`/g, (_, content) => addPlaceholder(`<code>${escapeHtml(content)}</code>`))

  // Images: ![alt](url) — only allow same-origin (paths starting with /)
  processed = processed.replace(/!\[([^\]]*)\]\((\/[^)]+)\)/g, (_, alt, url) => {
    return addPlaceholder(`<img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}" class="md-inline-img" />`)
  })

  // Links: allowlisted schemes only; anything else renders as plain text
  processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label, url) => {
    var safeLabel = escapeHtml(label)
    var safeUrl = escapeHtml(url.trim())

    if (/^tel:\+?[\d\s\-().]+$/.test(url.trim())) {
      return addPlaceholder('<a href="' + safeUrl + '" class="md-link md-link-tel">\u{1F4DE}\u00A0' + safeLabel + '</a>')
    }
    if (/^sms:\+?[\d\s\-().]+$/.test(url.trim())) {
      return addPlaceholder('<a href="' + safeUrl + '" class="md-link md-link-sms">\u{1F4AC}\u00A0' + safeLabel + '</a>')
    }
    if (/^help:[a-f0-9]+$/.test(url.trim())) {
      var docId = escapeHtml(url.trim().slice(5))
      return addPlaceholder('<a href="/help_center/' + docId + '" class="md-link md-link-help" data-help-article="true">\u2139\uFE0F\u00A0' + safeLabel + '</a>')
    }
    if (/^\/[a-zA-Z0-9_\-/]*$/.test(url.trim())) {
      return addPlaceholder('<a href="' + safeUrl + '" class="md-link md-link-nav" data-spa-nav="true">' + safeLabel + '</a>')
    }

    return escapeHtml(label)
  })

  processed = processed.replace(/\*\*(.+?)\*\*/g, (_, content) => addPlaceholder(`<strong>${escapeHtml(content)}</strong>`))
  processed = processed.replace(/(^|[\s(])\*([^*]+?)\*(?=[\s).,;:!?]|$)/g, (_, prefix, content) => {
    return `${prefix}${addPlaceholder(`<em>${escapeHtml(content)}</em>`)}`
  })

  let escaped = escapeHtml(processed)
  placeholders.forEach(({ key, html }) => {
    const pattern = new RegExp(escapeForRegExp(key), 'g')
    escaped = escaped.replace(pattern, html)
  })
  return escaped
}

export function renderMarkdownToHtml(text, options = {}) {
  if (!text) return ''

  const settings = {
    stripAssistantClosings: false,
    addLastElementClass: false,
    ...options,
  }

  const lines = String(text).replace(/\r\n/g, '\n').split('\n')
  const result = []

  let listStack = []
  let listContent = []
  let inCodeBlock = false
  let codeFenceLanguage = ''
  let codeLines = []

  const closeOneList = () => {
    const popped = listStack.pop()
    if (popped) {
      listContent.push(`</${popped.type}>`)
    }
  }

  const flushAllLists = () => {
    while (listStack.length > 0) {
      closeOneList()
    }
    if (listContent.length > 0) {
      result.push(listContent.join(''))
      listContent = []
    }
  }

  const closeListsForIndent = (indent) => {
    while (listStack.length > 0 && listStack[listStack.length - 1].indent > indent) {
      closeOneList()
    }
  }

  const ensureList = (type, indent) => {
    while (listStack.length > 0) {
      const current = listStack[listStack.length - 1]
      if (current.indent > indent) {
        closeOneList()
        continue
      }
      if (current.indent === indent && current.type !== type) {
        closeOneList()
        continue
      }
      break
    }
    const current = listStack.length > 0 ? listStack[listStack.length - 1] : null
    if (!current || current.indent < indent || current.type !== type) {
      listStack.push({ type, indent })
      listContent.push(`<${type}>`)
    }
  }

  const flushCodeBlock = () => {
    if (!inCodeBlock) return
    flushAllLists()
    const langClass = codeFenceLanguage ? ` class="language-${escapeHtml(codeFenceLanguage)}"` : ''
    result.push(`<pre><code${langClass}>${escapeHtml(codeLines.join('\n'))}</code></pre>`)
    inCodeBlock = false
    codeFenceLanguage = ''
    codeLines = []
  }

  let i = 0
  while (i < lines.length) {
    const originalLine = lines[i]
    const line = String(originalLine || '').trim()
    const indent = getIndentLevel(originalLine)

    const fenceMatch = line.match(/^```(\w+)?\s*$/)
    if (fenceMatch) {
      if (inCodeBlock) {
        flushCodeBlock()
      } else {
        flushAllLists()
        inCodeBlock = true
        codeFenceLanguage = (fenceMatch[1] || '').trim()
        codeLines = []
      }
      i += 1
      continue
    }

    if (inCodeBlock) {
      codeLines.push(String(originalLine || ''))
      i += 1
      continue
    }

    if (isTableRow(line) && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
      flushAllLists()
      const headerCells = splitTableCells(line).map(cell => renderInlineMarkdown(cell))
      const bodyRows = []
      i += 2
      while (i < lines.length && isTableRow(lines[i])) {
        const rowCells = splitTableCells(lines[i]).map(cell => renderInlineMarkdown(cell))
        bodyRows.push(rowCells)
        i += 1
      }

      const thead = `<thead><tr>${headerCells.map(cell => `<th>${cell}</th>`).join('')}</tr></thead>`
      const tbody = bodyRows.length > 0
        ? `<tbody>${bodyRows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}</tbody>`
        : ''
      result.push(`<table class="markdown-table">${thead}${tbody}</table>`)
      continue
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/)
    if (headingMatch) {
      flushAllLists()
      const level = headingMatch[1].length
      const headingText = renderInlineMarkdown(headingMatch[2])
      result.push(`<h${level}>${headingText}</h${level}>`)
      i += 1
      continue
    }

    const numberedMatch = line.match(/^(\d+)[.)]\s+(.+)$/)
    if (numberedMatch) {
      const content = renderInlineMarkdown(numberedMatch[2])
      closeListsForIndent(indent)
      ensureList('ol', indent)
      listContent.push(`<li>${content}</li>`)
      i += 1
      continue
    }

    const bulletMatch = line.match(/^[-*]\s+(.+)$/)
    if (bulletMatch) {
      const content = renderInlineMarkdown(bulletMatch[1])
      closeListsForIndent(indent)
      ensureList('ul', indent)
      listContent.push(`<li>${content}</li>`)
      i += 1
      continue
    }

    if (!line) {
      i += 1
      continue
    }

    flushAllLists()
    result.push(`<p>${renderInlineMarkdown(line)}</p>`)
    i += 1
  }

  flushCodeBlock()
  flushAllLists()

  let html = result.join('')

  if (settings.stripAssistantClosings) {
    const closingPatterns = [
      /if you (?:need|require|want|would like|'d like)[^<]*?[!.]/gi,
      /(?:please )?let me know[^<]*?[!.]/gi,
      /(?:please )?feel free[^<]*?[!.]/gi,
      /i(?:'m| am) here (?:to help|if you)[^<]*?[!.]/gi,
      /is there anything else[^<]*?[!.]/gi,
      /don't hesitate to[^<]*?[!.]/gi,
    ]
    closingPatterns.forEach((pattern) => {
      html = html.replace(pattern, '')
    })
  }

  html = html.replace(/<p[^>]*>\s*<\/p>/gi, '')
  html = html.replace(/<p[^>]*>\s*&nbsp;\s*<\/p>/gi, '')
  html = html.trim()

  if (settings.addLastElementClass) {
    const blockElementRegex = /<(p|ul|ol|h[1-6]|table|pre)([^>]*)>/g
    const matches = [...html.matchAll(blockElementRegex)]
    if (matches.length > 0) {
      const lastMatch = matches[matches.length - 1]
      const tagName = lastMatch[1]
      const attrs = lastMatch[2] || ''
      const className = tagName === 'p'
        ? 'last-paragraph'
        : (tagName.match(/^h[1-6]$/) ? 'last-heading' : 'last-element')
      let newTag = `<${tagName}`
      if (attrs.includes('class=')) {
        newTag += attrs
          .replace(/class="([^"]*)"/, `class="$1 ${className}"`)
          .replace(/class='([^']*)'/, `class='$1 ${className}'`)
      } else {
        newTag += `${attrs} class="${className}"`
      }
      newTag += '>'
      const before = html.substring(0, lastMatch.index)
      const after = html.substring(lastMatch.index + lastMatch[0].length)
      html = before + newTag + after
    }
  }

  return html
}
